import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Stack,
} from "@mui/material";
import { createDictionaryFromText } from "../utils/api";
import { authHeaders } from "../utils/api";


export default function ImportSnowflake() {
  const [yamlInput, setYamlInput] = useState("");
  const [converted, setConverted] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [stage, setStage] = useState(1); // 1=upload, 2=preview, 3=confirm

  const handleConvert = async () => {
      if (!yamlInput.trim()) return;
      setLoading(true);
      try {
        const headers = await authHeaders(); // ✅ includes Authorization: Bearer <token>
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE}/semantic/convert`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...headers,
            },
            body: JSON.stringify({ yaml: yamlInput }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        setConverted(text);
        setStage(2);
      } catch (e) {
        alert("Conversion failed: " + e.message);
      } finally {
        setLoading(false);
      }
    };

  const handleSave = async () => {
    if (!converted || !name.trim()) return;
    setLoading(true);
    try {
      const created = await createDictionaryFromText(name.trim(), converted);
      const newId = created?.id || created?.dict_id;
      window.location.href = `/dictionaries/${newId}`;
    } catch (e) {
      alert("Failed to save: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Import from Snowflake YAML
      </Typography>

      {/* STEP 1: Upload or paste YAML */}
      {stage === 1 && (
        <Stack spacing={2}>
          <Typography>
            Paste your Snowflake semantic YAML below (from <code>CALL SYSTEM$READ_YAML_FROM_SEMANTIC_VIEW()</code> or a file):
          </Typography>
          <TextField
            multiline
            minRows={14}
            value={yamlInput}
            onChange={(e) => setYamlInput(e.target.value)}
            placeholder="Paste semantic YAML here..."
            fullWidth
            sx={{ fontFamily: "monospace" }}
          />
          <Button
            variant="contained"
            onClick={handleConvert}
            disabled={loading || !yamlInput.trim()}
          >
            {loading ? "Converting..." : "Convert to Dictionary Format"}
          </Button>
        </Stack>
      )}

      {/* STEP 2: Preview Conversion */}
      {stage === 2 && (
        <Stack spacing={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            Conversion Preview
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                bgcolor: "#fafafa",
                fontFamily: "monospace",
                fontSize: 13,
                whiteSpace: "pre-wrap",
                overflow: "auto",
              }}
            >
              <Typography variant="subtitle2" mb={1} fontWeight={600}>
                Original YAML
              </Typography>
              {yamlInput}
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                bgcolor: "#f8fff8",
                fontFamily: "monospace",
                fontSize: 13,
                whiteSpace: "pre-wrap",
                overflow: "auto",
              }}
            >
              <Typography variant="subtitle2" mb={1} fontWeight={600}>
                Converted Data Dictionary Format
              </Typography>
              {loading ? <CircularProgress size={20} /> : converted}
            </Paper>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setStage(1)}>Back</Button>
            <Button variant="contained" onClick={() => setStage(3)}>
              Continue
            </Button>
          </Stack>
        </Stack>
      )}

      {/* STEP 3: Confirm + Import */}
      {stage === 3 && (
        <Stack spacing={2}>
          <Typography>Enter a name for your new data dictionary:</Typography>
          <TextField
            label="Dictionary Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setStage(2)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !name.trim()}
            >
              {loading ? "Importing..." : "Import"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}